import { useEffect } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import Theme from "@components/Theme";
import { useCountry } from "@util/useCountry";
import { applyPricingGeoOverrides } from "@util/pricingGeoOverrides";
import AppStorage from "@components/AppStorage";
import StickyPlanComparision from "@components/organisms/pricing/StickyPlanComparision";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";
import GetProFreePlan from "../../components/organisms/common/GetProFreePlan.js";
import { FeaturesGridSection } from "pages/features/payments.js";
import TpWidgetSlider from "../../components/organisms/common/TpWidgetSlider.js";
const TpWidget = dynamic(() => import("@components/atoms/document/TpWidget"));
const DoubleCTA = dynamic(() =>
  import("@components/organisms/common/DoubleCTA")
);
const InjectChatSupport = dynamic(() =>
  import("@components/atoms/InjectChatSupport")
);
const Schema = dynamic(() => import("@components/organisms/common/Schema"));
const WideCTA = dynamic(() => import("@components/organisms/common/WideCTA"));
const CTA = dynamic(() => import("@components/organisms/common/CTA"));
const FAQ = dynamic(() => import("@components/organisms/common/FAQ"));

export async function getStaticProps(ctx) {
  const { locale } = ctx;
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/pricing/root/marketing.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/pricing/root/content.json`
  );

  // Catch Error
  if (
    !headerResponse.ok ||
    !footerResponse.ok ||
    !marketingResponse.ok ||
    !contentResponse.ok
  ) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
  };

  return {
    props: {
      data,
      locale,
      fallback: false,
    },
  };
}

export default function Index({ data }) {
  const { seo, header, footer, content: baseContent } = data;
  const country = useCountry();
  const content = applyPricingGeoOverrides(baseContent, country);
  useEffect(() => {
    const tableObserver = () => {
      const headers = document.querySelectorAll(".table-header");
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const header = entry.target;
            const tableContent = header.closest(".table-content");
            const isLargeDevice = window.innerWidth >= 620;

            // Trigger when header hits 50% of viewport height
            if (
              entry.intersectionRatio === 0 &&
              entry.boundingClientRect.top < window.innerHeight / 2 &&
              isLargeDevice
            ) {
              header.classList.add("sticky");

              // Calculate remaining height from sticky point
              const headerHeight = header.offsetHeight;
              const remainingHeight = window.innerHeight - headerHeight;
              if (isLargeDevice && tableContent) {
                const contentHeight = tableContent.scrollHeight;
                if (remainingHeight < contentHeight) {
                  tableContent.style.maxHeight = `${contentHeight}px`;
                } else {
                  tableContent.style.maxHeight = `${remainingHeight}px`;
                }
              }
            } else if (
              entry.isIntersecting &&
              entry.boundingClientRect.top > window.innerHeight / 2 &&
              isLargeDevice
            ) {
              header.classList.remove("sticky");
              if (tableContent) {
                tableContent.style.maxHeight = "none";
              }
            }
          });
        },
        {
          root: null,
          rootMargin: `-${window.innerHeight / 2}px 0px 0px 0px`,
          threshold: 0,
        }
      );

      headers.forEach((header) => observer.observe(header));
    };
    tableObserver();
  });

  return (
    <>
      <Head>
        <meta
          name="apple-itunes-app"
          content="app-id=665859578, app-argument=https://apps.apple.com/us/app/setmore-appointment-scheduling/id665859578?pid=ios_smart_banner&c=ios_campaign"
        />
      </Head>
      <Theme
        header={header}
        footer={footer}
        seo={seo}
        className="pricing-layout pricing_active pricing-lander-page"
      >
        <AppStorage pageName="Pricing Root Page" />
        <section className="bg-light-blue-grey pt-8 lg:pt-10">
          <GetProFreePlan
            content={{
              ...content.pricingPlanContent,
              smsDisclaimer: content.smsDisclaimer,
            }}
            pricing={content.pricingPlan}
            initialPlanType={content.initialPlanType}
            className="pricing-wrap"
            isHero={true}
            hasGeoOverrides={!baseContent.geoExclude?.includes(country)}
          />
        </section>
        <section className="bg-light-blue-grey essential-fold pb-10 ">
          <h2 className="container text-lg lg:pb-10 md:pb-5 text-left">
            All essentials included
          </h2>
          <FeaturesGridSection
            data={content.featuresGrid}
            isIconInline={true}
          />
        </section>
        <section className="pricing-layout-v2" id="pricing-table">
          <h2 className="container text-lg lg:pb-10 text-left">
            Your growth plan starts here
          </h2>
          <StickyPlanComparision
            categories={content.pricingComparison.categories}
            planCost={content.pricingComparison.planCost}
            ctaButtons={content.pricingComparison.ctaButtons}
            initialPlanType={content.initialPlanType}
          />
        </section>
        <section className="bg-light-blue-grey">
          <TpWidgetSlider />
        </section>
        {content.hasOwnProperty("paymentCta") ? (
          <CTA
            data={content.paymentCta}
            className="pos-rel bg-light-blue-grey md:pb-10 lg:pb-20 cta"
            ctaBgLightGreenColor={true}
          />
        ) : null}
        {content.hasOwnProperty("doubleCTA") ? (
          <>
            <DoubleCTA
              data={content.doubleCTA}
              className="lg:pt-5 lg:pb-20 md:pt-2.5 md:pb-10"
            />
          </>
        ) : null}
        {content.hasOwnProperty("cta") ? (
          <CTA
            className="lg:pt-8 lg:pb-10 md:pb-10 bg-light-blue-grey"
            data={content.cta}
            wrapperClass="cta-article"
            ctaBgDarkGreenColor={true}
          />
        ) : null}
        <FAQ
          className="lg:pb-20 lg:pt-10 bg-light-blue-grey  md:pb-10"
          data={content.faqs}
        />
        <WideCTA data={content.wideCTA} />
        <InjectChatSupport />
        <AppStorage pageName="Pricing" lang="EN" />
        <TpWidget />
      </Theme>
      <Schema data={content.faqs.list} />
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
        .pricing-layout-v2 {
          background-color: ${colors.primitive.grey[600]};
        }
        .faq-section {
          padding-top: 0px !important;
        }
        .p-block span {
          width: 92% !important;
        }
        .p-block li {
          width: 80% !important;
          text-align: center !important;
          margin: 0 !important;
        }
        .chatsupport-button {
          visibility: visible !important;
        }
        .cta-contrast.has-noimage article {
          max-width: 760px;
        }
        .cta-light-green-theme h2 {
          font-size: ${font.size.lg[1]} !important;
          line-height: ${lineHeight[10]} !important;
        }
        .cta-light-green-theme .btn-quaternary {
          border: 1px solid ${colors.primitive.green[800]};
          color: ${colors.primitive.green[800]};
        }
        .cta-light-green-theme .cta-link {
          border-bottom: 1px solid ${colors.primitive.white.default};
        }
        .cta-light-green-theme .btn-ternary {
          background-color: ${colors.primitive.green[800]};
          color: ${colors.primitive.white.default};
        }
        .cta-light-green-theme .btn-ternary:hover {
          background-color: ${colors.primitive.green[900]};
        }
        .cta-light-green-theme .btn-quaternary:hover {
          background-color: ${colors.primitive.green[800]};
          color: ${colors.primitive.white.default};
        }
        .bg-offset-top::before {
          top: 80px;
          background-color: ${colors.primitive.grey[600]} !important;
        }
        .cta-article {
          max-width: 450px;
        }
        @media (min-width: ${viewport.tablet.sm}) {
          .pr-card .abs--img-card {
            top: 98px;
            left: 92px;
          }
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .bg-offset-top::before {
            height: 320px;
            top: 40px;
          }
          .faq-section h2 {
            margin-bottom: 40px !important;
          }
        }
      `}</style>
    </>
  );
}
